import { useCallback, useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { formatCredits } from '@/lib/creditCosts';

const ACTION_LABELS: Record<string, string> = {
  content_generate: 'Content generation',
  visual_generate: 'Visual generation',
  campaign_trigger: 'Campaign trigger',
  campaign_launch: 'Campaign launch',
  campaign_generate: 'Workflow generation',
};
const actionLabel = (a: string) => ACTION_LABELS[a] || a;

interface Bucket { action?: string; id?: string; name?: string; email?: string; credits: number; count: number; }
interface RecentRow {
  id: string;
  action: string;
  credits: number;
  admin: { id: string; name: string; email: string } | null;
  workspace: { id: string; name: string } | null;
  createdAt: string;
}
interface UsageResponse {
  summary: { totalCredits: number; totalActions: number; activeAdmins: number };
  byAction: Bucket[];
  byAdmin: Bucket[];
  byWorkspace: Bucket[];
  recent: RecentRow[];
  capped: boolean;
}

export default function AdminUsage() {
  const [data, setData] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch<UsageResponse>('/admin/usage')
      .then(setData)
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Failed to load usage'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const summaryCards = [
    { label: 'Credits used', value: formatCredits(data?.summary.totalCredits ?? 0) },
    { label: 'Actions', value: (data?.summary.totalActions ?? 0).toLocaleString() },
    { label: 'Active admins', value: (data?.summary.activeAdmins ?? 0).toLocaleString() },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1 container mx-auto max-w-6xl py-10 px-4">
        <div className="mb-8">
          <h1 className="text-2xl font-bold">Super-Admin Usage</h1>
          <p className="text-sm text-muted-foreground">
            Super admins bypass all credit limits. This is what they would have been charged, tracked in credits.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {summaryCards.map((c) => (
                <Card key={c.label} className="p-6">
                  <p className="text-sm text-muted-foreground">{c.label}</p>
                  <p className="text-3xl font-bold mt-1">{c.value}</p>
                </Card>
              ))}
            </div>

            {data?.capped && (
              <p className="text-xs text-amber-600">
                Showing the most recent 5,000 events — older usage is excluded from these totals.
              </p>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h2 className="text-sm font-semibold mb-2">By action</h2>
                <Card className="p-0 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Action</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">Credits</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data?.byAction.length ?? 0) === 0 ? (
                        <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No usage yet</TableCell></TableRow>
                      ) : (
                        data?.byAction.map((b) => (
                          <TableRow key={b.action}>
                            <TableCell>{actionLabel(b.action || '')}</TableCell>
                            <TableCell className="text-right">{b.count.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-medium">{formatCredits(b.credits)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </div>

              <div>
                <h2 className="text-sm font-semibold mb-2">By admin</h2>
                <Card className="p-0 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Admin</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                        <TableHead className="text-right">Credits</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data?.byAdmin.length ?? 0) === 0 ? (
                        <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">No usage yet</TableCell></TableRow>
                      ) : (
                        data?.byAdmin.map((b) => (
                          <TableRow key={b.id || b.email}>
                            <TableCell>
                              <div className="font-medium">{b.name}</div>
                              <div className="text-xs text-muted-foreground">{b.email}</div>
                            </TableCell>
                            <TableCell className="text-right">{b.count.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-medium">{formatCredits(b.credits)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            </div>

            <div>
              <h2 className="text-sm font-semibold mb-2">Recent activity</h2>
              <Card className="p-0 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Admin</TableHead>
                      <TableHead>Workspace</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead className="text-right">Credits</TableHead>
                      <TableHead>When</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data?.recent.length ?? 0) === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No usage recorded yet</TableCell></TableRow>
                    ) : (
                      data?.recent.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium">{r.admin?.name || '—'}</TableCell>
                          <TableCell>{r.workspace?.name || '—'}</TableCell>
                          <TableCell><Badge variant="secondary">{actionLabel(r.action)}</Badge></TableCell>
                          <TableCell className="text-right">{formatCredits(r.credits)}</TableCell>
                          <TableCell className="text-muted-foreground">{new Date(r.createdAt).toLocaleString()}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
